import { useEffect, useMemo, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { whenFree } from "@/lib/popupQueue";
import { playIntroChime } from "@/lib/celebrate";
import { HeartFrameAvatar } from "./SupporterHeartArt";
import { defaultNote } from "./SupporterThanks";

const COLORS = ["text-rose-500", "text-pink-400", "text-amber-400", "text-rose-300", "text-pink-500", "text-amber-300"];
const since = (s) => new Date(s).toLocaleDateString("de-DE", { month: "long", year: "numeric" });

function HeartRain() {
  const hearts = useMemo(() => Array.from({ length: 30 }, (_, i) => ({ l: Math.random() * 100, s: 12 + Math.random() * 22, d: 4.5 + Math.random() * 4, delay: Math.random() * 5, c: COLORS[i % COLORS.length] })), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-testid="supporter-welcome-rain">
      {hearts.map((h, i) => <Heart key={i} className={`sp-rain ${h.c}`} fill="currentColor" strokeWidth={0} style={{ left: `${h.l}%`, width: h.s, height: h.s, animationDuration: `${h.d}s`, animationDelay: `${h.delay}s` }} />)}
    </div>
  );
}

export function SupporterWelcome({ p }) {
  const [w, setW] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/vip/welcome").then(({ data }) => {
      if (!data.show) return;
      setW(data);
      whenFree("sp-welcome", () => { setOpen(true); playIntroChime(); api.post("/vip/welcome/seen").catch(() => {}); });
    }).catch(() => {});
  }, []);
  if (!open || !w) return null;
  const first = p.name?.split(" ")[0];
  return (
    <div role="dialog" aria-label="Willkommen als Projekt Unterstützer" className="fixed inset-0 z-[80] flex items-center justify-center p-4 sp-welcome-bg backdrop-blur-md fade-up" data-testid="supporter-welcome">
      <div className="relative w-full max-w-md max-h-[92vh] overflow-y-auto rounded-[2rem] sp-card px-5 pt-4 pb-6 text-center sp-welcome-pop">
        <div className="flex justify-center"><HeartFrameAvatar p={p} /></div>
        <p className="-mt-2 text-[11px] font-extrabold tracking-[0.3em] text-rose-500">WILLKOMMEN IM HERZEN DER COMMUNITY</p>
        <h2 className="font-display text-2xl sm:text-3xl font-black mt-1 leading-tight" data-testid="supporter-welcome-title"><span className="sp-name">Danke, {first}!</span> ❤️</h2>
        {w.since && <p className="mt-1 text-xs font-semibold text-[#9a5470]" data-testid="supporter-welcome-since">Projekt Unterstützer seit {since(w.since)}</p>}
        <blockquote className="mt-4 rounded-2xl sp-quote px-4 py-4 font-hand text-[22px] leading-snug text-[#5b1f35] text-left whitespace-pre-line" data-testid="supporter-welcome-note">
          „{w.note || defaultNote(p.name)}“
          <span className="block mt-2 text-right text-2xl text-rose-600">Saskia & Stefan ❤️</span>
        </blockquote>
        <button onClick={() => setOpen(false)} data-testid="supporter-welcome-close" className="mt-5 w-full h-12 rounded-full pink-gradient text-white font-extrabold shadow-lg shadow-rose-300/60 inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <Heart className="w-5 h-5" fill="currentColor" /> Mein Herz-Profil ansehen
        </button>
      </div>
      <HeartRain />
    </div>
  );
}
